/**
 * Admin de TA → Bot (Sol 28/09/2026): ver y editar los system prompts de los
 * dos agentes de la sección (intake: prompt + knowledge de JD; Agente TA
 * Metrics: prompt), con "restaurar el de la spec". Los textos de la spec siguen
 * en el código; lo que se guarda es un override (lib/ta-agent-prompts.ts) que
 * los agentes toman en el próximo turno. Molde: TaRulesCard (reglas de cálculo).
 */
import { currentLocale } from '@/lib/locale';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Pencil, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { cn } from '@/lib/utils';
import { useSaveTaAgentPrompts, useTaAgentPrompts } from '@/hooks/useTalentAcquisition';
import type { TaAgentPromptKey } from '@/../../shared/types/ta-metrics';

const KEYS: TaAgentPromptKey[] = ['intakePrompt', 'intakeKnowledge', 'metricsPrompt'];

export function TaAgentPromptsCard() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.agent_prompts';
  const prompts = useTaAgentPrompts();
  const save = useSaveTaAgentPrompts();
  const [key, setKey] = useState<TaAgentPromptKey>('intakePrompt');
  /** null = viendo el texto efectivo; string = editando. */
  const [draft, setDraft] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);

  const view = prompts.data;
  const effective = view?.effective[key] ?? '';
  const overridden = view?.overridden[key] ?? false;

  // Cambió el texto elegido o llegó una versión nueva: se descarta lo editado
  useEffect(() => { setDraft(null); setConfirmRestore(false); }, [key, view?.updatedAt]);

  const normalized = (s: string) => s.replace(/\r\n/g, '\n').trim();
  const dirty = draft !== null && normalized(draft) !== normalized(effective);
  const shown = draft ?? effective;

  if (prompts.isLoading) return <div className="flex justify-center py-8"><WinclapLoader /></div>;
  if (prompts.isError || !view) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <p className="flex items-center gap-1.5 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />{t(`${k}.load_error`)}</p>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => prompts.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" /> {t('talent_acquisition.rules.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle>
        <CardDescription>{t(`${k}.desc`)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {KEYS.map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => setKey(x)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm transition-colors',
                key === x ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-muted',
              )}
            >
              {t(`${k}.tab_${x}`)}
              {view.overridden[x] && <span className="ml-1.5 text-[10px] uppercase opacity-80">{t(`${k}.badge_edited`)}</span>}
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">{t(`${k}.help_${key}`)}</p>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {overridden && view.updatedAt
              ? t(`${k}.overridden_by`, { by: view.updatedBy || '—', at: new Date(view.updatedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }) })
              : t(`${k}.using_spec`)}
            {' · '}
            {t(`${k}.chars`, { n: shown.length.toLocaleString(currentLocale()) })}
          </p>
          <div className="flex flex-wrap gap-2">
            {draft === null ? (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setDraft(effective)}>
                <Pencil className="h-3.5 w-3.5" /> {t(`${k}.edit`)}
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={save.isPending}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={() => save.mutate({ [key]: draft }, { onSuccess: () => setDraft(null) })} disabled={!dirty || save.isPending}>
                  {save.isPending ? t('talent_acquisition.governance.saving') : t(`${k}.save`)}
                </Button>
              </>
            )}
            {overridden && draft === null && !confirmRestore && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirmRestore(true)} disabled={save.isPending}>
                <RotateCcw className="h-3.5 w-3.5" /> {t(`${k}.restore`)}
              </Button>
            )}
            {confirmRestore && (
              <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs dark:border-amber-800 dark:bg-amber-950">
                <span>{t(`${k}.restore_confirm`)}</span>
                <Button size="sm" variant="destructive" className="h-7" onClick={() => save.mutate({ [key]: '' }, { onSuccess: () => setConfirmRestore(false) })} disabled={save.isPending}>
                  {t('common.yes')}
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setConfirmRestore(false)}>{t('common.no')}</Button>
              </div>
            )}
          </div>
        </div>

        <Textarea
          value={shown}
          onChange={(e) => setDraft(e.target.value)}
          readOnly={draft === null}
          rows={24}
          spellCheck={false}
          className={cn('font-mono text-xs', draft === null && 'bg-muted/40')}
        />

        <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t(`${k}.warning`)}
        </p>
      </CardContent>
    </Card>
  );
}
